import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { buildApiUrl } from '../../utils/apiUrl';
import { useAppPermisos } from '../../hooks/useAppPermisos';
import type { AppRol, AppUsuarioRol } from '../../types/empacadora';

interface UsuarioEmpresa {
  user_id: string;
  email: string | null;
  nombre: string;
  telefono: string;
  email_confirmed_at?: string | null;
  last_sign_in_at?: string | null;
  invited_at?: string | null;
  roles: { id: number; codigo: string; nombre: string; color: string; perm_vob: boolean }[];
}

// ── Permisos disponibles ──────────────────────────────────────────────────────

const PERMISOS: { key: keyof AppRol; label: string; desc: string }[] = [
  { key: 'perm_generar_boletas',      label: 'Generar boletas',       desc: 'Crear y editar boletas del plan semanal' },
  { key: 'perm_boletas_labores',      label: 'Boletas de labores',    desc: 'Crear boletas de labores (no da acceso a boletas de aplicación)' },
  { key: 'perm_cierre_planilla',      label: 'Cierre de planilla',    desc: 'Aprobar o anular el cierre semanal de finca que se envía a Planilla' },
  { key: 'perm_despachar',            label: 'Despachar',             desc: 'Preparar y despachar productos desde bodega' },
  { key: 'perm_confirmar_aplicacion', label: 'Confirmar aplicación',  desc: 'Confirmar que la aplicación fue completada' },
  { key: 'perm_reporte_campo',        label: 'Reporte de campo',      desc: 'Llenar datos del boom: RPM, presión, clima, etc.' },
  { key: 'perm_vob',                  label: 'V°B° Ingeniero',        desc: 'Dar visto bueno y archivar boletas' },
  { key: 'perm_ver_catalogo',         label: 'Ver catálogo',          desc: 'Ver y editar el Catálogo de Productos (inventario)' },
  { key: 'perm_anular',               label: 'Anular boletas',        desc: 'Cancelar boletas con motivo' },
  { key: 'perm_ver_reportes',         label: 'Ver reportes',          desc: 'Acceso a dashboards y reportes de ejecución' },
  { key: 'perm_muestrear',            label: 'Muestrear',             desc: 'Ver el programa de muestreos y registrar resultados de campo' },
  { key: 'perm_administrar',          label: 'Administrar módulo',    desc: 'Configuración de roles y usuarios' },
  { key: 'perm_sync_vfp',             label: 'Sincronizar VFP',       desc: 'Ejecutar la sincronización de catálogo/costos con VFP' },
];

const MARCHAS = ['1ra', '2da', '3ra', '4ta', 'N/A'];

const ROL_VACIO: Omit<AppRol, 'id' | 'created_at' | 'updated_at'> = {
  empresa_id: 0,
  codigo: '',
  nombre: '',
  descripcion: '',
  color: '#3b82f6',
  icono: 'User',
  activo: true,
  perm_generar_boletas: false,
  perm_despachar: false,
  perm_confirmar_aplicacion: false,
  perm_reporte_campo: false,
  perm_vob: false,
  perm_ver_catalogo: false,
  perm_anular: false,
  perm_ver_reportes: false,
  perm_muestrear: false,
  perm_administrar: false,
  perm_sync_vfp: false,
  perm_boletas_labores: false,
  perm_cierre_planilla: false,
};

// ── Componente ────────────────────────────────────────────────────────────────

export function ControlPinaRolesPage() {
  const empresaId = useEmpresaId();
  const { perm_administrar, loading: permisosLoading } = useAppPermisos();

  const [tab, setTab]           = useState<'roles' | 'usuarios' | 'datos'>('roles');
  const [roles, setRoles]       = useState<AppRol[]>([]);
  const [usuarios, setUsuarios] = useState<AppUsuarioRol[]>([]);
  const [datosUsuarios, setDatosUsuarios] = useState<UsuarioEmpresa[]>([]);
  const [editUser, setEditUser] = useState<UsuarioEmpresa | null>(null);
  const [editForm, setEditForm] = useState<{ nombre: string; telefono: string }>({ nombre: '', telefono: '' });
  const [loading, setLoading]   = useState(false);
  const [msg, setMsg]           = useState<{ tipo: 'ok' | 'err'; texto: string } | null>(null);

  // modal rol
  const [modalRol, setModalRol]   = useState<Partial<AppRol> | null>(null);
  const [guardando, setGuardando] = useState(false);

  // modal asignar usuario
  const [modalUser, setModalUser]           = useState(false);
  const [allUsers, setAllUsers]             = useState<{ id: string; email: string; nombre: string }[]>([]);
  const [userSelId, setUserSelId]           = useState('');
  const [rolSelId, setRolSelId]             = useState(0);

  // modal invitar nuevo usuario
  interface ColaboradorOpt {
    id: number;
    nombre: string;
    cedula: string | null;
    email: string | null;
    telefono: string | null;
    labor: string | null;
    user_id: string | null;
    vinculado_aplicaciones: boolean;
  }
  const [modalInvitar, setModalInvitar] = useState(false);
  const [colaboradores, setColaboradores] = useState<ColaboradorOpt[]>([]);
  const [colaboradorSel, setColaboradorSel] = useState<number | null>(null);
  const [invitarForm, setInvitarForm]   = useState<{ email: string; nombre: string; telefono: string; rol_id: number; colaborador_id: number | null }>({
    email: '', nombre: '', telefono: '', rol_id: 0, colaborador_id: null,
  });

  // modal reenviar invitación
  const [modalReenviar, setModalReenviar] = useState<UsuarioEmpresa | null>(null);
  const [reenviando, setReenviando] = useState(false);

  function showMsg(tipo: 'ok' | 'err', texto: string) {
    setMsg({ tipo, texto });
    setTimeout(() => setMsg(null), 3500);
  }

  // ── Cargar roles ────────────────────────────────────────────────────────────

  const cargarRoles = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('app_roles')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('nombre');
    setRoles((data ?? []) as AppRol[]);
    setLoading(false);
  }, [empresaId]);

  // ── Cargar asignaciones de usuario ─────────────────────────────────────────

  const cargarUsuarios = useCallback(async () => {
    setLoading(true);
    const [{ data: roles }, { data: { session } }] = await Promise.all([
      supabase
        .from('app_usuario_roles')
        .select('*, rol:app_roles(id, nombre, color, codigo)')
        .eq('empresa_id', empresaId)
        .eq('activo', true)
        .order('created_at', { ascending: false }),
      supabase.auth.getSession(),
    ]);
    // Enriquecer con nombre/email desde el endpoint
    let nombrePorUser: Record<string, string> = {};
    try {
      const resp = await fetch(buildApiUrl('/aplicaciones/usuarios-empresa'), {
        headers: {
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
      });
      const j = await resp.json().catch(() => ({}));
      if (resp.ok && Array.isArray(j.usuarios)) {
        nombrePorUser = Object.fromEntries(
          j.usuarios.map((u: UsuarioEmpresa) => [u.user_id, u.nombre || u.email || u.user_id.slice(0, 8)])
        );
      }
    } catch { /* fallback al ID corto */ }
    const enriquecidos = (roles ?? []).map((r: any) => ({
      ...r,
      user_nombre: nombrePorUser[r.user_id] || (r.user_nombre as string) || r.user_id.slice(0, 8),
    }));
    setUsuarios(enriquecidos as AppUsuarioRol[]);
    setLoading(false);
  }, [empresaId]);

  // ── Cargar datos editables (nombre, telefono) ──────────────────────────────

  const cargarDatosUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl('/aplicaciones/usuarios-empresa'), {
        headers: {
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || 'Error al cargar usuarios');
      setDatosUsuarios((j.usuarios || []) as UsuarioEmpresa[]);
    } catch (e: any) {
      showMsg('err', e?.message || 'No se pudo cargar la lista');
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  async function guardarDatosUsuario() {
    if (!editUser) return;
    setGuardando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/usuarios-empresa/${editUser.user_id}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify(editForm),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || 'No se pudo actualizar');
      showMsg('ok', 'Datos actualizados.');
      setEditUser(null);
      cargarDatosUsuarios();
    } catch (e: any) {
      showMsg('err', e?.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  }

  useEffect(() => {
    cargarRoles();
    cargarUsuarios();
  }, [cargarRoles, cargarUsuarios]);

  useEffect(() => {
    if (tab === 'datos') cargarDatosUsuarios();
  }, [tab, cargarDatosUsuarios]);

  // ── Guardar rol ─────────────────────────────────────────────────────────────

  async function guardarRol() {
    if (!modalRol) return;
    if (!modalRol.nombre?.trim() || !modalRol.codigo?.trim()) {
      showMsg('err', 'Nombre y código son obligatorios.'); return;
    }
    setGuardando(true);
    const payload = { ...modalRol, empresa_id: empresaId, updated_at: new Date().toISOString() };
    const { error } = modalRol.id
      ? await supabase.from('app_roles').update(payload).eq('id', modalRol.id)
      : await supabase.from('app_roles').insert({ ...payload });
    setGuardando(false);
    if (error) { showMsg('err', error.message); return; }
    showMsg('ok', modalRol.id ? 'Rol actualizado.' : 'Rol creado.');
    setModalRol(null);
    cargarRoles();
  }

  // ── Eliminar asignación usuario ─────────────────────────────────────────────

  async function quitarRolUsuario(id: number) {
    await supabase.from('app_usuario_roles').update({ activo: false }).eq('id', id);
    cargarUsuarios();
  }

  // ── Cargar usuarios del sistema para asignar ────────────────────────────────

  async function abrirModalUser() {
    // Usuarios del sistema de la empresa (con cuenta), para asignarles un rol.
    // Fuente: usuarios_empresas → usuarios (auth_user_id es el user_id que usa
    // app_usuario_roles). Antes usaba from('colaboradores') — esa tabla ya no
    // existe (pasó a pl_colaboradores) y dejó el select vacío.
    const { data: ue } = await supabase
      .from('usuarios_empresas')
      .select('usuario:usuarios(auth_user_id, nombre, email)')
      .eq('empresa_id', empresaId)
      .eq('activo', true);
    const lista = (ue ?? [])
      .map((r: any) => r.usuario)
      .filter((u: any) => u && u.auth_user_id)
      .map((u: any) => ({ id: u.auth_user_id as string, email: u.email || '', nombre: u.nombre || u.email || '' }))
      // ordenar por nombre
      .sort((a: any, b: any) => (a.nombre || '').localeCompare(b.nombre || ''));
    setAllUsers(lista);
    setUserSelId('');
    setRolSelId(roles[0]?.id ?? 0);
    setModalUser(true);
  }

  async function confirmarReenvio() {
    if (!modalReenviar) return;
    const u = modalReenviar;
    setReenviando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl(`/aplicaciones/usuarios-empresa/${u.user_id}/reenviar-invitacion`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ enviar_whatsapp: true }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || 'No se pudo reenviar');
      const partes = ['Invitación reenviada por email'];
      if (j.whatsapp_enviado) partes.push('y WhatsApp ✓');
      else if (j.tiene_telefono && !j.whatsapp_configurado) partes.push('(WhatsApp no configurado en el servidor)');
      else if (!j.tiene_telefono) partes.push('(sin teléfono, no se envió WhatsApp)');
      showMsg('ok', partes.join(' '));
      setModalReenviar(null);
    } catch (e: any) {
      showMsg('err', e?.message || 'Error al reenviar');
    } finally {
      setReenviando(false);
    }
  }

  async function abrirInvitar() {
    setInvitarForm({ email: '', nombre: '', telefono: '', rol_id: roles[0]?.id ?? 0, colaborador_id: null });
    setColaboradorSel(null);
    // Cargar colaboradores RRHH
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl('/aplicaciones/colaboradores-disponibles'), {
        headers: {
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
      });
      const j = await resp.json().catch(() => ({}));
      if (resp.ok && Array.isArray(j.colaboradores)) {
        setColaboradores(j.colaboradores);
      } else {
        setColaboradores([]);
      }
    } catch {
      setColaboradores([]);
    }
    setModalInvitar(true);
  }

  function seleccionarColaborador(colId: number | null) {
    setColaboradorSel(colId);
    if (colId == null) {
      setInvitarForm({ email: '', nombre: '', telefono: '', rol_id: invitarForm.rol_id, colaborador_id: null });
      return;
    }
    const c = colaboradores.find(x => x.id === colId);
    if (!c) return;
    setInvitarForm({
      email: c.email || '',
      nombre: c.nombre || '',
      telefono: (c.telefono || '').replace(/\D/g, ''),
      rol_id: invitarForm.rol_id,
      colaborador_id: c.id,
    });
  }

  async function invitarUsuario() {
    if (!invitarForm.email || !invitarForm.nombre || !invitarForm.rol_id) return;
    setGuardando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl('/aplicaciones/usuarios-empresa/invitar'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify(invitarForm),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || 'No se pudo invitar');
      if (j.ya_existia) {
        showMsg('ok', `Usuario ya existía. Rol asignado a ${invitarForm.email}.`);
      } else if (j.invitacion_enviada) {
        showMsg('ok', `Invitación enviada a ${invitarForm.email}. Revisará su email para configurar contraseña.`);
      } else {
        showMsg('ok', 'Usuario creado y rol asignado.');
      }
      setModalInvitar(false);
      setInvitarForm({ email: '', nombre: '', telefono: '', rol_id: 0, colaborador_id: null });
      setColaboradorSel(null);
      cargarDatosUsuarios();
    } catch (e: any) {
      showMsg('err', e?.message || 'Error al invitar');
    } finally {
      setGuardando(false);
    }
  }

  async function asignarRol() {
    if (!userSelId || !rolSelId) return;
    setGuardando(true);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from('app_usuario_roles').upsert({
      empresa_id: empresaId,
      user_id: userSelId,
      rol_id: rolSelId,
      activo: true,
      asignado_por: (user?.user_metadata?.nombre as string) || user?.email || '',
    }, { onConflict: 'empresa_id,user_id,rol_id' });
    setGuardando(false);
    if (error) { showMsg('err', error.message); return; }
    showMsg('ok', 'Rol asignado.');
    setModalUser(false);
    cargarUsuarios();
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (permisosLoading) return null;

  if (!perm_administrar) {
    return (
      <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#f8fafc', margin: 0 }}>Roles y Permisos</h2>
        <p style={{ fontSize: '14px', color: 'var(--ink-muted)', margin: '8px 0 0' }}>
          Esta pantalla requiere permiso de administración del módulo.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>

      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#f8fafc', margin: 0 }}>
          Control de Piña — Roles y Permisos
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--ink-muted)', margin: '4px 0 0' }}>
          Configure los roles del módulo y asigne usuarios
        </p>
      </div>

      {msg && (
        <div style={{
          padding: '12px 16px', borderRadius: '10px', marginBottom: '16px',
          fontWeight: 600, fontSize: '14px',
          background: msg.tipo === 'ok' ? '#0f2c20' : '#34181c',
          border: `1px solid ${msg.tipo === 'ok' ? '#1d6e4f' : '#7d2f3a'}`,
          color: msg.tipo === 'ok' ? '#9df4c7' : '#ffb3bb',
        }}>{msg.texto}</div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {(['roles', 'usuarios', 'datos'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
            cursor: 'pointer', border: `1.5px solid ${tab === t ? '#6366f1' : 'var(--line)'}`,
            background: tab === t ? 'rgba(99,102,241,0.15)' : 'transparent',
            color: tab === t ? '#a5b4fc' : 'var(--ink-muted)',
          }}>
            {t === 'roles' ? 'Roles' : t === 'usuarios' ? 'Usuarios asignados' : 'Datos (nombre / WhatsApp)'}
          </button>
        ))}
      </div>

      {/* ── Tab Roles ── */}
      {tab === 'roles' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button onClick={() => setModalRol({ ...ROL_VACIO, empresa_id: empresaId })} style={{
              padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: 700,
              border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer',
            }}>+ Nuevo rol</button>
          </div>

          {loading && <p style={{ color: 'var(--ink-muted)', textAlign: 'center', padding: '40px' }}>Cargando...</p>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {roles.map(r => (
              <div key={r.id} style={{
                padding: '16px 18px', borderRadius: '12px',
                border: `1.5px solid ${r.activo ? r.color + '55' : 'var(--line)'}`,
                background: 'var(--surface-raised)',
                opacity: r.activo ? 1 : 0.5,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '10px', height: '10px', borderRadius: '50%',
                      background: r.color, flexShrink: 0,
                    }} />
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#f8fafc' }}>{r.nombre}</div>
                      <div style={{ fontSize: '12px', color: 'var(--ink-faint)', fontFamily: 'monospace' }}>{r.codigo}</div>
                      {r.descripcion && <div style={{ fontSize: '13px', color: 'var(--ink-muted)', marginTop: '2px' }}>{r.descripcion}</div>}
                    </div>
                  </div>
                  <button onClick={() => setModalRol(r)} style={{
                    padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
                    border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
                  }}>Editar</button>
                </div>

                {/* Permisos activos */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px' }}>
                  {PERMISOS.filter(p => r[p.key] === true).map(p => (
                    <span key={p.key} style={{
                      padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
                      background: r.color + '22', color: r.color, border: `1px solid ${r.color}44`,
                    }}>{p.label}</span>
                  ))}
                  {PERMISOS.filter(p => r[p.key] === true).length === 0 && (
                    <span style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>Sin permisos asignados</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Tab Usuarios ── */}
      {tab === 'usuarios' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button onClick={abrirModalUser} style={{
              padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: 700,
              border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer',
            }}>+ Asignar usuario</button>
          </div>

          {loading && <p style={{ color: 'var(--ink-muted)', textAlign: 'center', padding: '40px' }}>Cargando...</p>}

          {/* Agrupado por usuario */}
          {(() => {
            const porUser: Record<string, AppUsuarioRol[]> = {};
            usuarios.forEach(u => {
              if (!porUser[u.user_id]) porUser[u.user_id] = [];
              porUser[u.user_id].push(u);
            });
            return Object.entries(porUser).map(([userId, asigs]) => (
              <div key={userId} style={{
                padding: '14px 16px', borderRadius: '12px', marginBottom: '10px',
                border: '1px solid var(--line)', background: 'var(--surface-raised)',
              }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#f8fafc', marginBottom: '10px' }}>
                  {asigs[0]?.user_nombre || userId.slice(0, 8) + '...'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {asigs.map(a => {
                    const rol = a.rol as any;
                    return (
                      <div key={a.id} style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '4px 10px 4px 12px', borderRadius: '999px',
                        background: (rol?.color ?? '#6366f1') + '22',
                        border: `1px solid ${(rol?.color ?? '#6366f1')}44`,
                      }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: rol?.color ?? '#a5b4fc' }}>
                          {rol?.nombre ?? '—'}
                        </span>
                        <button onClick={() => quitarRolUsuario(a.id!)} style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'rgba(255,255,255,0.3)', fontSize: '14px', lineHeight: 1, padding: '0 2px',
                        }}>×</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ));
          })()}

          {!loading && usuarios.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-muted)' }}>
              No hay usuarios con roles asignados
            </div>
          )}
        </>
      )}

      {/* ── Tab Datos ── */}
      {tab === 'datos' && (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '16px' }}>
            <p style={{ fontSize: '13px', color: 'var(--ink-faint)', fontStyle: 'italic', margin: 0, flex: 1 }}>
              Actualice el nombre y número de WhatsApp de los usuarios. Estos datos se usan para las notificaciones de ajustes desde Bodega.
            </p>
            <button
              onClick={abrirInvitar}
              style={{
                padding: '9px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 700,
                border: 'none', background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                color: '#fff', cursor: 'pointer', flexShrink: 0,
              }}>
              + Invitar usuario
            </button>
          </div>
          {loading && <p style={{ color: 'var(--ink-muted)', textAlign: 'center', padding: '40px' }}>Cargando...</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {datosUsuarios.map(u => (
              <div key={u.user_id} style={{
                padding: '14px 16px', borderRadius: '12px',
                border: '1px solid var(--line)', background: 'var(--surface-raised)',
                display: 'flex', alignItems: 'center', gap: '12px',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#f8fafc' }}>
                      {u.nombre || u.email || u.user_id.slice(0, 8) + '...'}
                    </div>
                    {(() => {
                      // Estado de cuenta
                      const confirmado = !!u.email_confirmed_at;
                      const haEntrado  = !!u.last_sign_in_at;
                      if (confirmado && haEntrado) {
                        return (
                          <span title={`Último ingreso: ${new Date(u.last_sign_in_at!).toLocaleString('es-CR')}`} style={{
                            fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '999px',
                            border: '1px solid #22c55e', background: 'rgba(34,197,94,0.12)', color: '#4ade80',
                            textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
                          }}>✓ Activo</span>
                        );
                      }
                      if (confirmado) {
                        return (
                          <span title="Confirmó email pero aún no ha ingresado" style={{
                            fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '999px',
                            border: '1px solid #3b82f6', background: 'rgba(59,130,246,0.12)', color: '#60a5fa',
                            textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
                          }}>Confirmado</span>
                        );
                      }
                      return (
                        <span title={u.invited_at ? `Invitado el ${new Date(u.invited_at).toLocaleString('es-CR')}` : 'Sin confirmar'} style={{
                          fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '999px',
                          border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.12)', color: '#fbbf24',
                          textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
                        }}>⏳ Pendiente confirmar</span>
                      );
                    })()}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--ink-faint)', marginTop: '2px' }}>
                    {u.email || 'sin email'}
                    {u.telefono ? <> &nbsp;·&nbsp; <span style={{ color: '#4ade80' }}>📱 {u.telefono}</span></> : <> &nbsp;·&nbsp; <span style={{ color: '#f97316' }}>sin teléfono</span></>}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                    {u.roles.map(r => (
                      <span key={r.id} style={{
                        padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                        background: r.color + '22', color: r.color, border: `1px solid ${r.color}44`,
                      }}>
                        {r.nombre}{r.perm_vob ? ' ✓' : ''}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                  <button onClick={() => {
                    setEditUser(u);
                    setEditForm({ nombre: u.nombre, telefono: u.telefono });
                  }} style={{
                    padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                    border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
                  }}>Editar</button>
                  {!u.email_confirmed_at && u.email && (
                    <button onClick={() => setModalReenviar(u)} title={u.telefono ? 'Reenviar por email + WhatsApp' : 'Reenviar por email'} style={{
                      padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 700,
                      border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.1)',
                      color: '#fbbf24', cursor: 'pointer', whiteSpace: 'nowrap',
                    }}>
                      ↻ Reenviar invite{u.telefono ? ' + WS' : ''}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!loading && datosUsuarios.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-muted)' }}>
                No hay usuarios con roles activos en esta empresa
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Modal editar datos usuario ── */}
      {editUser && createPortal(
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
          <div style={{
            width: '100%', maxWidth: '420px', borderRadius: '18px', padding: '28px',
            background: '#0d1117', border: '1px solid var(--line)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
          }}>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#f8fafc', marginBottom: '6px' }}>
              Editar datos
            </div>
            <div style={{ fontSize: '13px', color: 'var(--ink-faint)', marginBottom: '20px', fontFamily: 'monospace' }}>
              {editUser.email}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>NOMBRE</label>
              <input value={editForm.nombre} onChange={e => setEditForm(p => ({ ...p, nombre: e.target.value }))}
                placeholder="Nombre completo"
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', border: '1px solid var(--line)', background: 'var(--surface-base)', color: '#f8fafc', outline: 'none', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>WHATSAPP</label>
              <input value={editForm.telefono} onChange={e => setEditForm(p => ({ ...p, telefono: e.target.value }))}
                placeholder="50683790976"
                inputMode="numeric"
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', border: '1px solid var(--line)', background: 'var(--surface-base)', color: '#f8fafc', outline: 'none', fontFamily: 'monospace', boxSizing: 'border-box' }} />
              <div style={{ fontSize: '11px', color: 'var(--ink-faint)', marginTop: '4px' }}>
                Formato internacional sin + (ej. 506 para Costa Rica)
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setEditUser(null)} style={{
                flex: 1, padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
              }}>Cancelar</button>
              <button onClick={guardarDatosUsuario} disabled={guardando} style={{
                flex: 2, padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: 700,
                border: 'none', background: guardando ? 'rgba(255,255,255,0.1)' : '#6366f1', color: '#fff', cursor: guardando ? 'not-allowed' : 'pointer',
              }}>{guardando ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Modal editar rol ── */}
      {modalRol && createPortal(
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
          <div style={{
            width: '100%', maxWidth: '540px', borderRadius: '18px', padding: '28px',
            background: '#0d1117', border: '1px solid var(--line)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.6)', maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#f8fafc', marginBottom: '20px' }}>
              {modalRol.id ? 'Editar rol' : 'Nuevo rol'}
            </div>

            {/* Nombre y código */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: '4px' }}>NOMBRE</label>
                <input value={modalRol.nombre ?? ''} onChange={e => setModalRol(p => ({ ...p!, nombre: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', border: '1px solid var(--line)', background: 'var(--surface-base)', color: '#f8fafc', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: '4px' }}>CÓDIGO</label>
                <input value={modalRol.codigo ?? ''} onChange={e => setModalRol(p => ({ ...p!, codigo: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', border: '1px solid var(--line)', background: 'var(--surface-base)', color: '#f8fafc', outline: 'none', fontFamily: 'monospace', boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Descripción */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: '4px' }}>DESCRIPCIÓN</label>
              <input value={modalRol.descripcion ?? ''} onChange={e => setModalRol(p => ({ ...p!, descripcion: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', border: '1px solid var(--line)', background: 'var(--surface-base)', color: '#f8fafc', outline: 'none', boxSizing: 'border-box' }} />
            </div>

            {/* Color */}
            <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600 }}>COLOR</label>
              <input type="color" value={modalRol.color ?? '#3b82f6'} onChange={e => setModalRol(p => ({ ...p!, color: e.target.value }))}
                style={{ width: '44px', height: '36px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'none' }} />
              <span style={{ fontSize: '13px', color: 'var(--ink-faint)', fontFamily: 'monospace' }}>{modalRol.color}</span>
              <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--ink-muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={modalRol.activo ?? true} onChange={e => setModalRol(p => ({ ...p!, activo: e.target.checked }))} />
                Activo
              </label>
            </div>

            {/* Permisos */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Permisos
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {PERMISOS.map(p => (
                  <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '10px 12px', borderRadius: '8px', background: (modalRol[p.key] as boolean) ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${(modalRol[p.key] as boolean) ? '#6366f155' : 'transparent'}`, transition: 'all 0.15s' }}>
                    <input type="checkbox" checked={(modalRol[p.key] as boolean) ?? false}
                      onChange={e => setModalRol(prev => ({ ...prev!, [p.key]: e.target.checked }))}
                      style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: modalRol.color ?? '#6366f1' }} />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#f8fafc' }}>{p.label}</div>
                      <div style={{ fontSize: '12px', color: 'var(--ink-muted)' }}>{p.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setModalRol(null)} style={{
                flex: 1, padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
              }}>Cancelar</button>
              <button onClick={guardarRol} disabled={guardando} style={{
                flex: 2, padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: 700,
                border: 'none', background: guardando ? 'rgba(255,255,255,0.1)' : '#6366f1', color: '#fff', cursor: guardando ? 'not-allowed' : 'pointer',
              }}>{guardando ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Modal reenviar invitación ── */}
      {modalReenviar && createPortal(
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
          <div style={{
            width: '100%', maxWidth: '440px', borderRadius: '18px', padding: '24px',
            background: '#0d1117', border: '1px solid var(--line)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
          }}>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#fbbf24', marginBottom: '8px' }}>
              ↻ Reenviar invitación
            </div>
            <p style={{ fontSize: '13px', color: 'var(--ink-muted)', marginBottom: '16px', lineHeight: 1.5 }}>
              Se generará un nuevo link de invitación (24h de validez) y se enviará a <strong style={{ color: '#f8fafc' }}>{modalReenviar.nombre || modalReenviar.email}</strong>.
            </p>
            <div style={{
              padding: '12px', borderRadius: '10px', background: 'var(--surface-base)',
              border: '1px solid var(--line)', marginBottom: '20px',
            }}>
              <div style={{ fontSize: '12px', color: 'var(--ink-muted)', marginBottom: '8px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Canales de envío
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#4ade80', marginBottom: '6px' }}>
                ✓ Email: {modalReenviar.email}
              </div>
              {modalReenviar.telefono ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#4ade80' }}>
                  ✓ WhatsApp: {modalReenviar.telefono}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#f97316' }}>
                  ⚠ Sin teléfono — solo se enviará por email
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalReenviar(null)} disabled={reenviando} style={{
                padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)',
                cursor: reenviando ? 'not-allowed' : 'pointer',
              }}>Cancelar</button>
              <button onClick={confirmarReenvio} disabled={reenviando} style={{
                padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: 800,
                border: 'none',
                background: reenviando ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg,#d97706,#fbbf24)',
                color: reenviando ? 'rgba(255,255,255,0.45)' : '#fff',
                cursor: reenviando ? 'not-allowed' : 'pointer',
              }}>
                {reenviando ? 'Enviando…' : 'Reenviar invitación'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Modal invitar nuevo usuario ── */}
      {modalInvitar && createPortal(
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
          <div style={{
            width: '100%', maxWidth: '460px', borderRadius: '18px', padding: '28px',
            background: '#0d1117', border: '1px solid var(--line)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
          }}>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#f8fafc', marginBottom: '6px' }}>
              Invitar nuevo usuario
            </div>
            <p style={{ fontSize: '12px', color: 'var(--ink-faint)', marginBottom: '16px' }}>
              Elegí un colaborador de RRHH para vincular, o cargá los datos manualmente. Se enviará un email de invitación con un link para que configure su contraseña.
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                COLABORADOR (desde RRHH)
              </label>
              <select
                value={colaboradorSel ?? ''}
                onChange={e => seleccionarColaborador(e.target.value ? Number(e.target.value) : null)}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
                  fontSize: '14px', outline: 'none',
                }}>
                <option value="">— Cargar manualmente (sin vincular) —</option>
                {colaboradores
                  .filter(c => !c.vinculado_aplicaciones)
                  .map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}{c.labor ? ` · ${c.labor}` : ''}{c.email ? ` · ${c.email}` : ' · (sin email)'}
                    </option>
                  ))}
              </select>
              <div style={{ fontSize: '10px', color: 'var(--ink-faint)', marginTop: '4px' }}>
                Solo se muestran colaboradores que aún no están vinculados a Aplicaciones.
                {colaboradorSel && ' Si el colaborador no tiene email/teléfono, complétalos abajo — se guardarán en RRHH.'}
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>EMAIL *</label>
              <input
                type="email"
                value={invitarForm.email}
                onChange={e => setInvitarForm({ ...invitarForm, email: e.target.value })}
                placeholder="ingeniero@empresa.com"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
                  fontSize: '14px', outline: 'none',
                }}
              />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>NOMBRE COMPLETO *</label>
              <input
                value={invitarForm.nombre}
                onChange={e => setInvitarForm({ ...invitarForm, nombre: e.target.value })}
                placeholder="Juan Pérez"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
                  fontSize: '14px', outline: 'none',
                }}
              />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>TELÉFONO (WhatsApp)</label>
              <input
                value={invitarForm.telefono}
                onChange={e => setInvitarForm({ ...invitarForm, telefono: e.target.value.replace(/\D/g, '') })}
                placeholder="50688887777"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
                  fontSize: '14px', outline: 'none',
                }}
              />
              <div style={{ fontSize: '10px', color: 'var(--ink-faint)', marginTop: '4px' }}>
                Con código país (sin + ni espacios). Para Costa Rica: 506xxxxxxxx
              </div>
            </div>

            <div style={{ marginBottom: '22px' }}>
              <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>ROL *</label>
              <select
                value={invitarForm.rol_id}
                onChange={e => setInvitarForm({ ...invitarForm, rol_id: Number(e.target.value) })}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
                  fontSize: '14px', outline: 'none',
                }}>
                <option value={0}>— Seleccionar rol —</option>
                {roles.filter(r => r.activo).map(r => (
                  <option key={r.id} value={r.id}>
                    {r.nombre}{r.perm_vob ? ' (V°B°)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalInvitar(false)} disabled={guardando} style={{
                padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)',
                cursor: guardando ? 'not-allowed' : 'pointer',
              }}>Cancelar</button>
              <button
                onClick={invitarUsuario}
                disabled={guardando || !invitarForm.email || !invitarForm.nombre || !invitarForm.rol_id}
                style={{
                  padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: 800,
                  border: 'none',
                  background: (guardando || !invitarForm.email || !invitarForm.nombre || !invitarForm.rol_id)
                    ? 'rgba(255,255,255,0.1)'
                    : 'linear-gradient(135deg,#16a34a,#22c55e)',
                  color: (guardando || !invitarForm.email || !invitarForm.nombre || !invitarForm.rol_id) ? 'rgba(255,255,255,0.45)' : '#fff',
                  cursor: (guardando || !invitarForm.email || !invitarForm.nombre || !invitarForm.rol_id) ? 'not-allowed' : 'pointer',
                }}>
                {guardando ? 'Enviando…' : 'Enviar invitación'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Modal asignar usuario ── */}
      {modalUser && createPortal(
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
          <div style={{
            width: '100%', maxWidth: '420px', borderRadius: '18px', padding: '28px',
            background: '#0d1117', border: '1px solid var(--line)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
          }}>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#f8fafc', marginBottom: '20px' }}>
              Asignar rol a usuario
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>USUARIO</label>
              <select value={userSelId} onChange={e => setUserSelId(e.target.value)} style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px',
                border: '1px solid var(--line)', background: 'var(--surface-base)', color: '#f8fafc',
              }}>
                <option value="">— Seleccione —</option>
                {allUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.nombre || u.email || u.id.slice(0, 8)}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>ROL</label>
              <select value={rolSelId} onChange={e => setRolSelId(Number(e.target.value))} style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px',
                border: '1px solid var(--line)', background: 'var(--surface-base)', color: '#f8fafc',
              }}>
                {roles.filter(r => r.activo).map(r => (
                  <option key={r.id} value={r.id}>{r.nombre}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setModalUser(false)} style={{
                flex: 1, padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
              }}>Cancelar</button>
              <button onClick={asignarRol} disabled={guardando || !userSelId || !rolSelId} style={{
                flex: 2, padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: 700,
                border: 'none', background: guardando || !userSelId || !rolSelId ? 'rgba(255,255,255,0.1)' : '#6366f1',
                color: '#fff', cursor: guardando || !userSelId || !rolSelId ? 'not-allowed' : 'pointer',
              }}>{guardando ? 'Guardando...' : 'Asignar'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
